import { Box, FormControl, Grid, Typography } from "@mui/material";
import { DateRange, DateRangePicker } from "@mui/x-date-pickers-pro";
import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { validateDate } from "../../app/common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import CitiesAndCountiesAutocomplete from "../../common/components/functional/fields/citiesAndCountiesAutocomplete/CitiesAndCountiesAutocomplete";
import { SearchFilter } from "../../common/components/functional/FilterSearchField/FilterSearchField";
import { CenteredGridItem } from "../../common/components/functional/FilterSearchField/styled";
import { RoundedButton } from "../../common/components/styled/styled";
import { CityAndCounty } from "../../common/types/apiTypes";
import { AutocompleteType } from "../../common/types/autoComplete";
import { fetchCustomerOrgs } from "../customer/customerOrganizationsSlice";
import {
  fetchExpertUsers,
  selectExpertUserOptions,
} from "../user/expertUsersSlice";
import { JOB_AD_CAMPAIGNS } from "./constants";
import { STATUS_TYPES } from "./types/common";
import {
  isoFormat,
  leadingZerosDateFormat,
} from "../../common/helpers/formatDateTime";
import { SelectAutocompleteSingle } from "../../common/components/functional/SelectAutocomplete/SelectAutocompleteSingle";
import {
  makeLookupValues,
  makeMultiSelectDisplay,
} from "../../common/helpers/filters";
import { SelectAutocompleteMulti } from "../../common/components/functional/SelectAutocomplete/SelectAutocompleteMulti";
import { parseFilters } from "./utils";
import { fetchRecruitmentAreas } from "../settings/recruitment/recruitmentsSlice";

export type FormValues = {
  dateRange: DateRange<Date>;
  area: AutocompleteType[];
  contactPerson: AutocompleteType[];
  locationCity: CityAndCounty[];
  customerCompanyName: AutocompleteType[];
  hasOrder: string;
  campaign: AutocompleteType[];
  status: AutocompleteType[];
};

interface Props {
  filters: SearchFilter[];
  updateFilters: (filters: SearchFilter[]) => void;
  handleMenuToggle: () => void;
}

export const JobAdsFilters = ({
  filters,
  updateFilters,
  handleMenuToggle,
}: Props) => {
  const { t } = useTranslation(["jobAds", "common"]);
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.user);
  const expertUserOptions = useAppSelector(selectExpertUserOptions);
  const meLabel = t("common:me");
  const contactPersonOptions = useMemo(
    () =>
      expertUserOptions
        .map((expert) => ({
          id: expert.id,
          name: expert.id === user?.id ? meLabel : expert.name,
        }))
        .sort((a, b) => {
          if (a.name === meLabel) return -1;
          if (b.name === meLabel) return 1;
          return a.name.localeCompare(b.name);
        }),
    [expertUserOptions, meLabel, user?.id],
  );

  const HAS_ORDER_OPTIONS = [
    { id: "true", name: t("true") },
    { id: "false", name: t("false") },
  ];
  const [customerOrgOptions, setCustomerOrgOptions] = useState<string[]>([]);
  const [recruitmentAreaOptions, setRecruitmentAreaOptions] = useState<
    AutocompleteType[]
  >([]);

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      dateRange: [null, null],
      area: [],
      contactPerson: [],
      locationCity: [],
      customerCompanyName: [],
      hasOrder: "",
      campaign: [],
      status: [],
    },
  });

  // Sync incoming filters prop into the form
  useEffect(() => {
    reset(parseFilters(filters));
  }, [filters, reset]);

  useEffect(() => {
    dispatch(fetchExpertUsers({}));
    dispatch(fetchRecruitmentAreas({})).then((action) => {
      if (isFulfilled(action)) {
        setRecruitmentAreaOptions(
          action.payload
            .map((area) => ({
              id: area.id,
              name: area.name,
            }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
      }
    });

    dispatch(fetchCustomerOrgs({})).then((action) => {
      if (isFulfilled(action)) {
        setCustomerOrgOptions(action.payload.map((customer) => customer.name));
      }
    });
  }, [dispatch]);

  const onSubmit = (data: FormValues) => {
    const {
      dateRange,
      area,
      contactPerson,
      locationCity,
      customerCompanyName,
      hasOrder,
      campaign,
      status,
    } = data;
    const { displayValue: areaDisplay, tooltip: areaTooltip } =
      makeMultiSelectDisplay(area);
    const {
      displayValue: contactPersonDisplay,
      tooltip: contactPersonTooltip,
    } = makeMultiSelectDisplay(contactPerson);
    const {
      displayValue: customerCompanyNameDisplay,
      tooltip: customerCompanyNameTooltip,
    } = makeMultiSelectDisplay(customerCompanyName);
    const { displayValue: campaignDisplay, tooltip: campaignTooltip } =
      makeMultiSelectDisplay(campaign);
    const { displayValue: statusDisplay, tooltip: statusTooltip } =
      makeMultiSelectDisplay(status);

    const newFilters: SearchFilter[] = [
      {
        name: "dateFrom",
        value:
          dateRange[0] && validateDate(dateRange[0], isoFormat)
            ? format(dateRange[0], isoFormat)
            : "",
        type: "date",
      },
      {
        name: "dateTo",
        value:
          dateRange[1] && validateDate(dateRange[1], isoFormat)
            ? format(dateRange[1], isoFormat)
            : "",
        type: "date",
      },
      {
        name: "area",
        value: area.map((a) => a.id),
        displayValue: areaDisplay,
        lookupValues: makeLookupValues(area),
        ...(areaTooltip && { tooltip: areaTooltip }),
      },
      {
        name: "contactPerson",
        value: contactPerson.map((person) => person.id),
        displayValue: contactPersonDisplay,
        lookupValues: makeLookupValues(contactPerson),
        ...(contactPersonTooltip && { tooltip: contactPersonTooltip }),
      },
      {
        name: "locationCity",
        value: locationCity?.map((city) => city.city) || "",
        lookupValues: locationCity.map((city) => ({
          id: city.city,
          name: city.county,
        })),
      },
      {
        name: "customerCompanyName",
        value: customerCompanyName.map((c) => c.id),
        displayValue: customerCompanyNameDisplay,
        lookupValues: makeLookupValues(customerCompanyName),
        ...(customerCompanyNameTooltip && {
          tooltip: customerCompanyNameTooltip,
        }),
      },
      { name: "hasOrder", value: hasOrder },
      {
        name: "campaign",
        value: campaign.map((c) => c.id),
        displayValue: campaignDisplay,
        lookupValues: makeLookupValues(campaign),
        ...(campaignTooltip && { tooltip: campaignTooltip }),
      },
      {
        name: "status",
        value: status.map((s) => s.id),
        displayValue: statusDisplay,
        lookupValues: makeLookupValues(status),
        ...(statusTooltip && { tooltip: statusTooltip }),
      },
    ];
    handleMenuToggle();
    updateFilters(newFilters);
  };

  return (
    <Box sx={{ marginTop: "20px" }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            {/* DATE */}
            <CenteredGridItem item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography align="right">{t("jobAdEndDate")}</Typography>
              </Grid>
              <Grid item xs={8}>
                <Controller
                  name="dateRange"
                  control={control}
                  render={({ field }) => (
                    <DateRangePicker
                      format={leadingZerosDateFormat}
                      value={field.value}
                      onChange={field.onChange}
                      slotProps={{
                        textField: {
                          size: "small",
                          label: null,
                        },
                      }}
                    />
                  )}
                />
              </Grid>
            </CenteredGridItem>

            {/* RECRUITMENT AREA */}
            <CenteredGridItem item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography align="right">{t("area")}</Typography>
              </Grid>
              <Grid item xs={8}>
                <FormControl fullWidth size="small">
                  <Controller
                    name="area"
                    control={control}
                    render={({ field }) => (
                      <SelectAutocompleteMulti
                        value={field.value}
                        onChange={(event) => {
                          field.onChange(event);
                        }}
                        options={recruitmentAreaOptions}
                        translationNamespaces={["jobAds"]}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </CenteredGridItem>

            {/* CONTACT PERSON */}
            <CenteredGridItem item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography align="right">{t("contactPerson")}</Typography>
              </Grid>
              <Grid item xs={8}>
                <FormControl fullWidth size="small">
                  <Controller
                    name="contactPerson"
                    control={control}
                    render={({ field }) => (
                      <SelectAutocompleteMulti
                        value={field.value}
                        onChange={(event) => {
                          field.onChange(event);
                        }}
                        options={contactPersonOptions}
                        translationNamespaces={["jobAds"]}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </CenteredGridItem>

            {/* LOCATION */}
            <CenteredGridItem item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography align="right">{t("locationCity")}</Typography>
              </Grid>
              <Grid item xs={8}>
                <Controller
                  name="locationCity"
                  control={control}
                  render={({ field }) => (
                    <CitiesAndCountiesAutocomplete
                      multiple
                      value={field.value}
                      onChange={(newValue: CityAndCounty[] | null) =>
                        field.onChange(newValue)
                      }
                    />
                  )}
                />
              </Grid>
            </CenteredGridItem>

            {/* SUBMIT */}
            <CenteredGridItem item container spacing={2} xs={12}>
              <Grid item xs={12}>
                <RoundedButton variant="contained" type="submit">
                  {t("searchJobAds")}
                </RoundedButton>
              </Grid>
            </CenteredGridItem>
          </Grid>

          <Grid item xs={6}>
            {/* CUSTOMER */}
            <CenteredGridItem item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography align="right">{t("customer")}</Typography>
              </Grid>
              <Grid item xs={8}>
                <FormControl fullWidth size="small">
                  <Controller
                    name="customerCompanyName"
                    control={control}
                    render={({ field }) => (
                      <SelectAutocompleteMulti
                        value={field.value}
                        onChange={(event) => {
                          field.onChange(event);
                        }}
                        options={customerOrgOptions.map((name) => ({
                          id: name,
                          name,
                        }))}
                        translationNamespaces={["jobAds"]}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </CenteredGridItem>

            {/* ORDER */}
            <CenteredGridItem item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography align="right">{t("hasOrder")}</Typography>
              </Grid>
              <Grid item xs={8}>
                <FormControl fullWidth size="small">
                  <Controller
                    name="hasOrder"
                    control={control}
                    render={({ field }) => (
                      <SelectAutocompleteSingle
                        value={
                          HAS_ORDER_OPTIONS.find(
                            (opt) => opt.id === field.value?.toString(),
                          ) || null
                        }
                        options={HAS_ORDER_OPTIONS}
                        onChange={(value) => {
                          field.onChange(value ? value.id : "");
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </CenteredGridItem>

            {/* CAMPAIGN */}
            <CenteredGridItem item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography align="right">{t("campaign")}</Typography>
              </Grid>
              <Grid item xs={8}>
                <FormControl fullWidth size="small">
                  <Controller
                    name="campaign"
                    control={control}
                    render={({ field }) => (
                      <SelectAutocompleteMulti
                        value={field.value}
                        options={JOB_AD_CAMPAIGNS.sort((a, b) =>
                          a.name.localeCompare(b.name),
                        )}
                        onChange={(value) => {
                          field.onChange(value);
                        }}
                        translationNamespaces={["jobAds"]}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </CenteredGridItem>

            {/* STATE */}
            <CenteredGridItem item container spacing={2} xs={12}>
              <Grid item xs={4}>
                <Typography align="right">{t("state")}</Typography>
              </Grid>
              <Grid item xs={8}>
                <FormControl fullWidth size="small">
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <SelectAutocompleteMulti
                        value={field.value}
                        onChange={(event) => {
                          field.onChange(event);
                        }}
                        options={STATUS_TYPES.map((status) => ({
                          id: status.id,
                          name: t(status.name),
                        }))}
                        translationNamespaces={["jobAds"]}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </CenteredGridItem>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
